/*
 *
 * ChannelsPage actions
 *
 */

import {
  SET_CLIENT_FILTER,
  SET_STATUS_FILTER,
  CLEAR_SAVE_ERROR,
  FETCH_CHANNELS_REQUEST,
  FETCH_CHANNELS_SUCCESS,
  FETCH_CHANNEL_DETAIL_REQUEST,
  FETCH_UPDATED_CHANNEL_DETAIL_REQUEST,
  FETCH_SLUG_VALIDATE_REQUEST,
  FETCH_SLUG_VALIDATE_SUCCESS,
  FETCH_SLUG_VALIDATE_FAILURE,
  FETCH_CHANNEL_DETAIL_SUCCESS,
  FETCH_CHANNEL_DETAIL_FAILURE,
  FETCH_CHANNELS_FAILURE,
  POST_CHANNEL_REQUEST,
  PUT_CHANNEL_SUCCESS,
  PUT_CHANNEL_FAILURE,
  FETCH_CLIENTS_REQUEST,
  FETCH_CLIENTS_SUCCESS,
  FETCH_CLIENTS_FAILURE,
  PATCH_CHANNEL_REQUEST,
  PATCH_CHANNEL_SUCCESS,
  CHANGE_TAB,
  FETCH_THEMES_REQUEST,
  FETCH_THEMES_SUCCESS,
  FETCH_THEMES_FAILURE,
  FETCH_MODES_REQUEST,
  FETCH_MODES_SUCCESS,
  FETCH_MODES_FAILURE,
  DELETE_CHANNEL_REQUEST,
  DELETE_CHANNEL_SUCCESS,
  DELETE_CHANNEL_FAILURE,
  ARCHIVE_CHANNEL_REQUEST,
  ARCHIVE_CHANNEL_SUCCESS,
  ARCHIVE_CHANNEL_FAILURE,
  SET_CHANEL_FORM,
} from './constants';

export const setClientFilter = (payload) => ({
  type: SET_CLIENT_FILTER,
  payload,
});

export const clearSaveError = () => ({ type: CLEAR_SAVE_ERROR });
export const setStatusFilter = (payload) => ({
  type: SET_STATUS_FILTER,
  payload,
});
export const changeTab = (payload) => ({
  type: CHANGE_TAB,
  payload,
});

export const fetchChannelsRequest = (params = null) => ({
  type: FETCH_CHANNELS_REQUEST,
  params,
});

export const fetchChannelsSuccess = (payload) => ({
  type: FETCH_CHANNELS_SUCCESS,
  payload
});

// Every detail request carries a monotonic id its response echoes back, so a
// response can only settle the request it belongs to. The initial fetch and the
// post-save fetch are separate saga watchers — `takeLatest` cancels within a
// watcher, never across them — and LeadsPage dispatches the initial fetch too,
// so two detail requests can be in flight at once and land in either order.
// Without the id, an older response overwrites a newer one's verdict.
let detailRequestSeq = 0;
export const nextDetailRequestId = () => (detailRequestSeq += 1);

export const fetchChannelDetailRequest = (
  channelId,
  requestId = nextDetailRequestId()
) => ({
  type: FETCH_CHANNEL_DETAIL_REQUEST,
  id: channelId,
  requestId,
});

export const fetchUpdatedChannelDetailRequest = (
  channelId,
  requestId = nextDetailRequestId()
) => ({
  type: FETCH_UPDATED_CHANNEL_DETAIL_REQUEST,
  id: channelId,
  requestId,
})

// Probe whether `slug` is free. `channelId` is the channel being edited, whose
// live slug AND `slug_history` the server excludes from the conflict search so
// its owner can reclaim one of their own former gallery URLs; omit it when
// creating a channel. (The field used to be named `id` but never held one — it
// always carried the candidate slug.)
export const validateChannelSlugRequest = (slug, channelId) => ({
  type: FETCH_SLUG_VALIDATE_REQUEST,
  slug,
  channelId,
});

export const validateChannelSlugFailure = (payload) => ({
  type: FETCH_SLUG_VALIDATE_FAILURE,
  payload
});

export const validateChannelSlugSuccess = (payload) => ({
  type: FETCH_SLUG_VALIDATE_SUCCESS,
  payload
});

export const fetchChannelDetailSuccess = (payload, requestId) => ({
  type: FETCH_CHANNEL_DETAIL_SUCCESS,
  payload,
  requestId
});

// A detail fetch failing is NOT the same event as the channel LIST failing, and
// the editor has to tell them apart: while a detail refresh is unresolved the
// form still holds the raw File objects from the last upload, so saving again
// would re-upload them. Keep it on its own action + its own state field rather
// than folding it into the shared `errors` (which no detail success clears).
export const fetchChannelDetailFailure = (error, requestId) => ({
  type: FETCH_CHANNEL_DETAIL_FAILURE,
  payload: error,
  requestId,
});

export const fetchChannelsFailure = () => ({
  type: FETCH_CHANNELS_FAILURE,
});

export const postChannelRequest = (payload) => ({
  type: POST_CHANNEL_REQUEST,
  payload
});

export const putChannelSuccess = (payload) => ({
  type: PUT_CHANNEL_SUCCESS,
  payload
});

export const putChannelFailure = (errors) => ({
  type: PUT_CHANNEL_FAILURE,
  payload: {
    errors
  }
});

export const fetchClientsRequest = ({ q }) => ({
  type: FETCH_CLIENTS_REQUEST,
  payload: {
    q,
  }
});

export const fetchClientsSuccess = (payload) => ({
  type: FETCH_CLIENTS_SUCCESS,
  payload
});

export const fetchClientsFailure = () => ({
  type: FETCH_CLIENTS_FAILURE,
});

export const patchChannelRequest = ({ id, data }) => ({
  type: PATCH_CHANNEL_REQUEST,
  payload: {
    id,
    data,
  }
});
export const patchChannelSuccess = (payload) => ({
  type: PATCH_CHANNEL_SUCCESS,
  payload
});

export const fetchThemesRequest = () => ({
  type: FETCH_THEMES_REQUEST,
});

export const fetchThemesSuccess = (payload) => ({
  type: FETCH_THEMES_SUCCESS,
  payload
});

export const fetchThemesFailure = () => ({
  type: FETCH_THEMES_FAILURE,
});

export const fetchModesRequest = () => ({
  type: FETCH_MODES_REQUEST,
});

export const fetchModesSuccess = (payload) => ({
  type: FETCH_MODES_SUCCESS,
  payload
});

export const fetchModesFailure = () => ({
  type: FETCH_MODES_FAILURE,
});

export const deleteChannelRequest = (payload) => ({
  type: DELETE_CHANNEL_REQUEST,
  channelId: payload,
  });

// Carry channelId so the reducer can remove the deleted row in place. The saga
// dispatches this with the id (not the API body) so DELETE_CHANNEL_SUCCESS's
// `allChannels.filter(item => item.id !== action.channelId)` actually drops the
// row rather than no-opping on an undefined id.
export const deleteChannelSuccess = (channelId) => ({
  type: DELETE_CHANNEL_SUCCESS,
  channelId,
});

export const deleteChannelFailure = () => ({
  type: DELETE_CHANNEL_FAILURE,
});

export const archiveChannelRequest = (payload) => ({
  type: ARCHIVE_CHANNEL_REQUEST,
  channelId: payload,
});

// Carry channelId so the reducer drops the archived channel from the active
// list in place (the active/archived status filter means an archived channel no
// longer belongs in the default `active` view).
export const archiveChannelSuccess = (channelId) => ({
  type: ARCHIVE_CHANNEL_SUCCESS,
  channelId,
});

export const archiveChannelFailure = () => ({
  type: ARCHIVE_CHANNEL_FAILURE,
});

export const setChannelForm = (payload) => ({
  type: SET_CHANEL_FORM,
  payload,
})